import { ApiConfig } from "common/models";
import { METHOD } from "common/config/endpoint.config";
import { buildGdxApiUrl } from "common/utils/api.util";
import {
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
  ListParams,
  CreateExtractionConfigurationRequest,
  UpdateExtractionConfigurationRequest
} from "../models/configuration-hub.model";

// Configuration Hub API endpoints
const ASSIGNMENT_CONTROLLER = "Assignment";
const DOCUMENT_TYPE_CONTROLLER = "DocumentType";
const CONFIGURATION_CONTROLLER = "Configuration";

export const configurationHubConfig = {
  // Assignment related configurations
  getAllAssignments: (listParams: ListParams) => {
    return new ApiConfig(
      buildGdxApiUrl(`${ASSIGNMENT_CONTROLLER}/${METHOD.LIST}`),
      "POST",
      "",
      listParams
    );
  },

  createAssignment: (assignment: CreateAssignmentRequest): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(ASSIGNMENT_CONTROLLER),
      "POST",
      undefined,
      assignment
    );
  },

  updateAssignment: (updateRequest: UpdateAssignmentRequest): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${ASSIGNMENT_CONTROLLER}/${updateRequest.id}`),
      "PUT",
      updateRequest.id.toString(),
      updateRequest
    );
  },

  deleteAssignment: (assignmentId: number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${ASSIGNMENT_CONTROLLER}/${assignmentId}`),
      "DELETE",
      assignmentId.toString(),
      undefined
    );
  },

  getAssignmentById: (assignmentId: number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${ASSIGNMENT_CONTROLLER}/${assignmentId}`),
      "GET",
      assignmentId.toString(),
      undefined
    );
  },

  // Document Type related configurations
  getAllDocumentTypes: () => {
    return new ApiConfig(
      buildGdxApiUrl(DOCUMENT_TYPE_CONTROLLER),
      "GET",
      "",
      undefined
    );
  },

  createDocumentType: (documentType: any): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(DOCUMENT_TYPE_CONTROLLER),
      "POST",
      undefined,
      documentType
    );
  },

  // Configuration Operations
  getConfigurationOperations: (assignmentId?: number) => {
    const endpoint = assignmentId
      ? `${CONFIGURATION_CONTROLLER}?assignmentId=${assignmentId}`
      : CONFIGURATION_CONTROLLER;

    return new ApiConfig(
      buildGdxApiUrl(endpoint),
      "GET",
      assignmentId?.toString() || "",
      undefined
    );
  },

  saveConfiguration: (configuration: any): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(CONFIGURATION_CONTROLLER),
      "POST",
      undefined,
      configuration
    );
  },

  // Get all database configurations
  getAllDatabaseConfigurations: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`/gdx/database/flowconfig/get-all`),
      "GET",
      "",
      undefined
    );
  },

  // Extraction Configuration related configurations
  getAllExtractionConfigurations: (listParams: ListParams) => {
    return new ApiConfig(
      buildGdxApiUrl(`ExtractionConfiguration/${METHOD.LIST}`),
      "POST",
      "",
      listParams
    );
  },

  createExtractionConfiguration: (
    configuration: CreateExtractionConfigurationRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl("ExtractionConfiguration"),
      "POST",
      undefined,
      configuration
    );
  },

  updateExtractionConfiguration: (
    updateRequest: UpdateExtractionConfigurationRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`ExtractionConfiguration/${updateRequest.id}`),
      "PUT",
      updateRequest.id.toString(),
      updateRequest
    );
  },

  deleteExtractionConfiguration: (configurationId: number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`ExtractionConfiguration/${configurationId}`),
      "DELETE",
      configurationId.toString(),
      undefined
    );
  },

  getExtractionConfigurationById: (configurationId: number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`ExtractionConfiguration/${configurationId}`),
      "GET",
      configurationId.toString(),
      undefined
    );
  },

  // Tag Library Configuration related configurations
  getAllTagLibraryConfigurations: (): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/tagLibrary/get-all`,
      "GET",
      "",
      undefined
    );
  },

  deleteTagLibraryConfiguration: (guidCode: string): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagLibrary/${guidCode}`,
      "DELETE",
      guidCode,
      undefined
    );
  },

  getTagConfigCardUpdate: (guidCode: string): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagConfigCardUpdate/${guidCode}`,
      "PUT",
      guidCode,
      undefined
    );
  },

  uploadTagLibrary: (formData: FormData): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/tagLibrary/upload`,
      "POST",
      undefined,
      formData
    );
  },

  // Tag Enrichment API configuration
  getTagEnrichmentDetails: (
    CFIHOSMasterObjectId: number,
    isActive: boolean
  ): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagEnrichment/getAllDetails?CFIHOSMasterObjectId=${CFIHOSMasterObjectId}&isActive=${isActive}`,
      "GET",
      undefined,
      undefined
    );
  },

  enrichTagDefinitions: (guidCodes: string[]): ApiConfig => {
    return new ApiConfig(
      `${process.env.REACT_APP_ENG_API_URL}/api/tagLibrary/tagdefinition_enrichment`,
      "POST",
      undefined,
      guidCodes
    );
  }
};
